// Browser sign-in via MSAL (redirect flow) → a Microsoft Graph client.
// Used when REACT_APP_AUTH_MODE=msal (the default for local/browser testing).
// Runs entirely in the browser against your Entra SPA app registration — no
// Teams runtime required. For the eventual Teams deployment, switch to the
// TeamsFx SSO path (REACT_APP_AUTH_MODE=teams) in useGraph.ts.

import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  type AccountInfo,
} from "@azure/msal-browser";
import { Client } from "@microsoft/microsoft-graph-client";
import { GRAPH_SCOPES } from "../config/leaveSystemConfig";

let pca: PublicClientApplication | null = null;

async function getPca(clientId: string, tenantId: string): Promise<PublicClientApplication> {
  if (pca) return pca;
  pca = new PublicClientApplication({
    auth: {
      clientId,
      authority: `https://login.microsoftonline.com/${tenantId}`,
      redirectUri: window.location.origin,
    },
    cache: { cacheLocation: "sessionStorage" },
  });
  await pca.initialize();
  // Complete any in-flight redirect sign-in and set the active account.
  const result = await pca.handleRedirectPromise();
  if (result?.account) pca.setActiveAccount(result.account);
  else {
    const existing = pca.getAllAccounts();
    if (existing.length) pca.setActiveAccount(existing[0]);
  }
  return pca;
}

/**
 * Acquire an access token for an arbitrary scope using the same MSAL instance
 * (and signed-in account) the app already uses for Graph. Used by the "Ask
 * Katie" bubble to get a Power Platform token (CopilotStudio.Copilots.Invoke).
 * Tries silent first; falls back to an interactive popup if consent/interaction
 * is required or no account is active yet.
 */
export async function acquireTokenForScope(
  clientId: string,
  tenantId: string,
  scope: string,
): Promise<string> {
  const p = await getPca(clientId, tenantId);
  const account = p.getActiveAccount() ?? p.getAllAccounts()[0];
  if (account) {
    try {
      const res = await p.acquireTokenSilent({ scopes: [scope], account });
      return res.accessToken;
    } catch (err) {
      if (!(err instanceof InteractionRequiredAuthError)) throw err;
    }
  }
  const res = await p.acquireTokenPopup({ scopes: [scope] });
  return res.accessToken;
}

export interface MsalGraph {
  /** The Graph client, or null when the user still needs to sign in. */
  client: Client | null;
  /** Kick off the redirect sign-in (navigates away, returns to the app). */
  signIn: () => Promise<void>;
  /** Sign the user out (clears the MSAL cache and returns to the app). */
  signOut: () => Promise<void>;
}

/**
 * Returns a Graph client if an account is already signed in, otherwise a
 * `signIn()` you can trigger from a button. Redirect flow avoids popup blockers.
 */
export async function getMsalGraph(clientId: string, tenantId: string): Promise<MsalGraph> {
  if (!clientId || !tenantId) {
    throw new Error(
      "MSAL needs REACT_APP_CLIENT_ID and REACT_APP_TENANT_ID (see env/.env.dev).",
    );
  }
  const p = await getPca(clientId, tenantId);
  const account = p.getActiveAccount();

  const signIn = async () => {
    await p.loginRedirect({ scopes: GRAPH_SCOPES });
  };

  const signOut = async () => {
    // Redirect logout clears MSAL's cache and returns to the app, which then
    // shows the sign-in screen again. postLogoutRedirectUri keeps us on-origin.
    await p.logoutRedirect({
      account: p.getActiveAccount() ?? undefined,
      postLogoutRedirectUri: window.location.origin,
    });
  };

  if (!account) return { client: null, signIn, signOut };
  return { client: buildClient(p, account), signIn, signOut };
}

function buildClient(p: PublicClientApplication, account: AccountInfo): Client {
  return Client.init({
    authProvider: async (done) => {
      try {
        const res = await p.acquireTokenSilent({ scopes: GRAPH_SCOPES, account });
        done(null, res.accessToken);
      } catch (err) {
        if (err instanceof InteractionRequiredAuthError) {
          // Silent failed (consent/expiry) — fall back to an interactive redirect.
          await p.acquireTokenRedirect({ scopes: GRAPH_SCOPES, account });
          done(err, null);
        } else {
          done(err as Error, null);
        }
      }
    },
  });
}
